<template>
  <div class="row">
    <!-- Tabs trạng thái -->
    <div class="col-12">
      <b-tabs v-model="statusTabIndex" @input="onStatusTab">
        <b-tab :title="$t('all')"></b-tab>
        <b-tab>
          <template #title>
            <span class="log-dot" style="background: #28a745"></span> {{ $t('success') }}
          </template>
        </b-tab>
        <b-tab>
          <template #title>
            <span class="log-dot" style="background: #dc3545"></span> {{ $t('error') }}
          </template>
        </b-tab>
      </b-tabs>
    </div>

    <!-- Filter -->
    <div class="col-12">
      <div class="tile">
        <div class="form-inline mb-2">
          <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="search"
                        :placeholder="$t('keyword')" style="min-width: 240px"></b-form-input>
          <client-select v-if="!client" :model="filterFormModel" attribute="client" @change="search" :isFilter="true"
                         :place-holder="$t('selectClient')" class="mr-1 mb-1"></client-select>
          <time-range-picker :model="filterFormModel" :defaultShortcut="0" :label="$t('createTime')"
                             class="mr-1 mb-1"></time-range-picker>
          <button @click="search" class="btn btn-primary mr-1 mb-1">
            <font-awesome-icon icon="search"/> {{ $t('search') }}
          </button>
          <button @click="resetAll" class="btn btn-secondary mr-1 mb-1">Reset</button>
        </div>
        <div class="form-inline">
          <small class="text-muted mr-2 mb-1">Lọc nâng cao:</small>
          <form-select class="mr-1 mb-1" :model="filterFormModel" attribute="type" :options="typeOptions"
                       @change="search"></form-select>
          <form-select class="mr-1 mb-1" :model="filterFormModel" attribute="action" :options="actionOptions"
                       @change="search"></form-select>
        </div>
      </div>
    </div>

    <!-- Paging top -->
    <div class="col-12">
      <div class="c-paging clearfix">
        <div class="c-paging__count" v-if="itemCount">
          {{ $t('found', [itemCount]) }}
        </div>
        <div class="c-paging__right">
          <div class="c-paging__numbers">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount"
                          :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Card list -->
    <div class="col-12" v-if="items.length">
      <div class="log-list">
        <div v-for="item in items" :key="item._id"
             class="log-card" :class="{ 'log-card--error': item.error }"
             :style="{ borderLeftColor: TYPE_COLORS[item.type] || '#888' }"
             @click="openDetail(item)" role="button">
          <div class="log-card__time">
            <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm:ss') }}</div>
            <div class="log-meta">{{ item.createTime | moment('YYYY') }}</div>
          </div>

          <div class="log-card__type">
            <span class="log-type-pill"
                  :style="{ background: TYPE_COLORS[item.type] || '#888' }">
              {{ typeLabel(item.type) }}
            </span>
            <div class="log-meta mt-1" v-if="item.action">{{ actionLabel(item.action) }}</div>
          </div>

          <div class="log-card__main">
            <div class="log-row-1 log-content"
                 v-html="contentSnippet(item)"></div>
          </div>

          <div class="log-card__people">
            <div v-if="item.client" class="log-meta-line">
              <font-awesome-icon icon="building" class="text-muted mr-1"/>
              {{ item.client.domain }}
            </div>
            <div v-if="item.user" class="log-meta-line">
              <font-awesome-icon icon="user" class="text-muted mr-1"/>
              {{ item.user.name }} <span class="log-meta">({{ item.user.ext }})</span>
            </div>
          </div>

          <div class="log-card__status">
            <b-badge v-if="item.error" variant="danger">{{ $t('error') }}</b-badge>
            <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
          </div>

          <div class="log-card__chev"><font-awesome-icon icon="chevron-right"/></div>
        </div>
      </div>
    </div>

    <div class="col-12 d-flex flex-column align-items-center text-muted py-4" v-if="!items.length">
      <font-awesome-icon icon="inbox" size="3x" class="mb-2"/>
      <span v-if="filterFormModel.page === 1 && !filterFormModel.keyword">{{ $t('notFoundCreate') }}</span>
      <span v-else>{{ $t('notFoundSearch') }}</span>
    </div>

    <div class="col-12" v-if="itemCount">
      <div class="c-paging clearfix">
        <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
        <div class="c-paging__right">
          <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                        v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                        @input="load"></b-pagination>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showModal" size="xl" hide-footer scrollable
             :modal-class="['log-modal']" body-class="log-modal-body">
      <template #modal-title>
        <span v-if="current">
          <span class="log-type-pill" :style="{ background: TYPE_COLORS[current.type] || '#888' }">
            {{ typeLabel(current.type) }}
          </span>
          <span class="ml-2">{{ $t('activity') }}</span>
          <span class="ml-2 small text-muted">{{ current.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </span>
      </template>

      <div v-if="current">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('status') }}</div>
            <b-badge v-if="current.error" variant="danger">{{ $t('error') }}</b-badge>
            <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('action') }}</div>
            <div>{{ actionLabel(current.action) || '—' }}</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('client') }}</div>
            <div v-if="current.client">{{ current.client.domain }}</div>
            <div v-else class="text-muted">—</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('createdBy') }}</div>
            <div v-if="current.user">{{ current.user.name }} <span class="log-meta">({{ current.user.ext }})</span></div>
            <div v-else class="text-muted">—</div>
          </div>
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">{{ $t('content') }}</div>
          <div class="log-content-block" v-html="(current.content || '').replace(/\n/g, '<br />')"></div>
        </div>

        <div class="log-section" v-if="current.type === 'webhook' && current.webhook">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="link" class="text-info mr-2"/> Webhook
          </div>
          <div class="mb-2"><b>Request</b> <code>{{ current.webhook.method }} {{ current.webhook.url }}</code></div>
          <json-viewer theme="dark-json-viewer" v-if="current.webhook.data"
                       :value="parseSafe(current.webhook.data)"
                       :expand-depth="20" :boxed="false" sort></json-viewer>
        </div>

        <div class="log-section" v-if="current.type === 'callChangeFile' && current.call">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="phone" class="text-success mr-2"/> {{ $t('recording') }}
          </div>
          <div class="mb-2">
            <label class="log-meta">{{ $t('recording') }}</label>
            <app-audio v-if="current.call.recordingDownloaded" :id="current.call._id"
                       :duration="current.call.duration" :log="current._id"/>
          </div>
          <div>
            <label class="log-meta">{{ $t('changeRecording') }}</label>
            <app-audio v-if="current.call.recordingDownloaded" :id="current.call._id"
                       :duration="current.call.duration"/>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import 'vue-json-viewer/style.css';
import JsonViewer from 'vue-json-viewer';
import List from '../../mixins/List';
import CRUD from "../../mixins/CRUD";
import TimeRangePicker from "../../components/TimeRangePicker";
import ClientSelect from '@/components/ClientSelect.vue'
import AppAudio from '@/components/AppAudio.vue'
import moment from 'moment'

// Màu badge theo type — phân biệt nhanh nhóm hoạt động
const TYPE_COLORS = {
  call: '#1976d2',
  callChangeFile: '#0d47a1',
  customer: '#43a047',
  opportunity: '#8e24aa',
  task: '#fb8c00',
  ticket: '#e53935',
  notification: '#fdd835',
  callCampaign: '#039be5',
  callCampaignContact: '#0288d1',
  messageCampaign: '#7e57c2',
  automation: '#00897b',
  user: '#607d8b',
  googleSheetSetup: '#34a853',
  clientKYC: '#3f51b5',
  phoneNumber: '#26c6da',
  creditTransaction: '#2e7d32',
  socialNetworkAccount: '#ec407a',
  aiAgent: '#d81b60',
  webhook: '#212121'
};

export default {
  components: {
    AppAudio,
    ClientSelect,
    JsonViewer, TimeRangePicker
  },
  props: ['client'],
  mixins: [List, CRUD],
  data() {
    return {
      showList: false,
      service: '/client-log',
      autoLoad: true,
      statusTabIndex: 0,
      TYPE_COLORS,
      showModal: false,
      current: null,
      defaultFilterFormModel: {
        status: null,
        type: null,
        action: null,
        client: this.client ? this.client._id : null,
        keyword: null,
        from: moment().startOf('day').valueOf(),
        to: moment().endOf('day').valueOf(),
      }
    }
  },
  computed: {
    typeOptions() {
      return [
        { value: null, text: this.$t('selectType') },
        { value: 'call', text: this.$t('call') },
        { value: 'callChangeFile', text: this.$t('callChangeFile') },
        { value: 'customer', text: this.$t('customer') },
        { value: 'opportunity', text: this.$t('opportunity') },
        { value: 'task', text: this.$t('task') },
        { value: 'ticket', text: this.$t('ticket') },
        { value: 'notification', text: this.$t('notification') },
        { value: 'callCampaign', text: this.$t('callCampaign') },
        { value: 'callCampaignContact', text: this.$t('callCampaignContact') },
        { value: 'messageCampaign', text: this.$t('messageCampaign') },
        { value: 'automation', text: this.$t('automation') },
        { value: 'user', text: this.$t('user') },
        { value: 'googleSheetSetup', text: this.$t('googleSheet') },
        { value: 'clientKYC', text: this.$t('clientKYC') },
        { value: 'phoneNumber', text: this.$t('phoneNumber') },
        { value: 'creditTransaction', text: this.$t('creditTransaction') },
        { value: 'socialNetworkAccount', text: this.$t('socialNetworkAccount') },
        { value: 'aiAgent', text: this.$t('aiAgent') },
        { value: 'webhook', text: 'Webhook' },
      ];
    },
    actionOptions() {
      return [
        { value: null, text: this.$t('selectAction') },
        { value: 'create', text: this.$t('create') },
        { value: 'update', text: this.$t('update') },
        { value: 'del', text: this.$t('delete') },
        { value: 'import', text: this.$t('import') },
        { value: 'export', text: this.$t('export') },
        { value: 'webhook', text: 'Webhook' },
      ];
    }
  },
  methods: {
    search() {
      this.filterFormModel.page = 1;
      this.load();
    },
    resetAll() {
      this.filterFormModel = Object.assign({}, this.defaultFilterFormModel);
      this.statusTabIndex = 0;
      this.load();
    },
    onStatusTab(idx) {
      this.filterFormModel.status = idx === 0 ? null : (idx === 1 ? 1 : 2);
      this.search();
    },
    openDetail(item) {
      this.current = item;
      this.showModal = true;
    },
    typeLabel(t) {
      if (!t) return '—';
      return this.$te(t) ? this.$t(t) : t;
    },
    actionLabel(a) {
      if (!a) return '';
      return this.$te(a) ? this.$t(a) : a;
    },
    contentSnippet(item) {
      const txt = (item.content || '').replace(/\n/g, ' ');
      return txt.length > 220 ? txt.substring(0, 220) + '…' : txt;
    },
    parseSafe(v) {
      if (!v) return {};
      try { return typeof v === 'string' ? JSON.parse(v) : v; } catch (e) { return { raw: v }; }
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All',
        activity: 'Activity',
        title: 'Client activity history',
        content: 'Content',
        createdBy: 'Created by',
        callCampaign: 'Call campaign',
        webhook: 'Webhook',
        selectStatus: 'Select status',
        selectType: 'Select type',
        selectAction: 'Select action',
        import: 'Import',
        call: 'Call',
        customer: 'Customer',
        opportunity: 'Opportunity',
        task: 'Task',
        ticket: 'Ticket',
        notification: 'Notification',
        callCampaignContact: 'Call campaign contact',
        messageCampaign: 'Message campaign',
        googleSheetSetup: 'Google sheet setup',
        automation: 'Automation',
        clientKYC: 'Profile',
        googleSheet: 'Google Sheet',
        recording: 'Recording file',
        changeRecording: 'Alternate recording file',
        callChangeFile: 'Change recording',
        creditTransaction: 'Credit transaction',
        socialNetworkAccount: 'Social network account',
        aiAgent: 'AI agent',
        phoneNumber: 'Phone number',
        user: 'User',
      },
      vi: {
        all: 'Tất cả',
        activity: 'Hoạt động',
        title: 'Lịch sử hoạt động của khách',
        content: 'Nội dung',
        createdBy: 'Tạo bởi',
        callCampaign: 'Chiến dịch gọi',
        webhook: 'Webhook',
        selectStatus: 'Chọn trạng thái',
        selectType: 'Chọn loại',
        selectAction: 'Chọn hành động',
        import: 'Tải lên',
        call: 'Cuộc gọi',
        customer: 'Khách hàng',
        opportunity: 'Cơ hội',
        task: 'Việc',
        ticket: 'Ticket',
        notification: 'Thông báo',
        callCampaignContact: 'Liên hệ chiến dịch',
        messageCampaign: 'Chiến dịch tin nhắn',
        googleSheetSetup: 'Google Sheet',
        automation: 'Tự động hoá',
        clientKYC: 'Hồ sơ',
        googleSheet: 'Google Sheet',
        recording: 'File ghi âm',
        changeRecording: 'File ghi âm thay thế',
        callChangeFile: 'Đổi file ghi âm',
        creditTransaction: 'Giao dịch',
        socialNetworkAccount: 'Tài khoản MXH',
        aiAgent: 'AI Agent',
        phoneNumber: 'Số điện thoại',
        user: 'Nhân viên',
      }
    }
  }
}
</script>

<style scoped>
.log-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.log-list { display: flex; flex-direction: column; gap: 8px; }

.log-card {
  display: flex;
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e8eaed;
  border-left: 4px solid #888;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s;
  gap: 16px;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06); transform: translateY(-1px); }
.log-card--error { background: #fef6f6; }

.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }
.log-meta-line { font-size: 12px; color: #555; }

.log-card__type {
  min-width: 130px;
  flex-shrink: 0;
}
.log-type-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.log-card__main {
  flex: 1;
  min-width: 0;
}
.log-content {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}
.log-content >>> b, .log-content >>> strong { color: #111; }

.log-card__people {
  min-width: 180px;
  flex-shrink: 0;
}

.log-card__status { display: flex; align-items: flex-start; flex-shrink: 0; padding-top: 2px; }
.log-card__chev { display: flex; align-items: center; color: #bbb; flex-shrink: 0; }

/* Modal */
.log-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}
.log-meta-label {
  font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;
}

.log-section { margin-top: 20px; }
.log-section__label {
  font-size: 12px; color: #888; text-transform: uppercase; margin-bottom: 6px;
}
.log-section__label--big {
  font-size: 14px; color: #222; text-transform: none; font-weight: 600;
  padding-bottom: 6px; border-bottom: 1px solid #e8eaed;
}
.log-content-block {
  background: #f6f8fa; padding: 12px; border-radius: 4px;
  word-break: break-word; font-size: 14px; line-height: 1.6;
}
</style>
<style>
.dark-json-viewer { background: #f8f8fb; }
.log-modal .modal-xl { max-width: 1200px; }
.log-modal .dark-json-viewer {
  max-height: 600px; overflow: auto; padding: 8px; font-size: 13px;
}
</style>
